/**
 * Voiceflow chat widget helpers.
 *
 * The widget is a third-party bundle that registers `window.voiceflow`, so the
 * script is injected once per page load and every caller shares that instance.
 */

const WIDGET_SRC = 'https://cdn.voiceflow.com/widget-next/bundle.mjs';

export const VOICEFLOW_PROJECT_ID =
  import.meta.env.REACT_APP_VOICEFLOW_PROJECT_ID || '6a55255e01ec64fffbc13de3';
export const VOICEFLOW_VERSION_ID = import.meta.env.REACT_APP_VOICEFLOW_VERSION_ID || '';
export const VOICEFLOW_RUNTIME_URL = 'https://general-runtime.voiceflow.com';
export const VOICEFLOW_VOICE_URL = 'https://runtime-api.voiceflow.com';

/** The variable the assistant branches on to pick a bot variant. */
export type VoiceflowUserRole = 'student' | 'coach' | 'parent';

interface VoiceflowChat {
  load: (config: Record<string, any>) => Promise<void>;
  destroy: () => void;
  open: () => void;
  close: () => void;
  interact: (action: { type: string; payload?: unknown }) => void;
}

declare global {
  interface Window {
    voiceflow?: { chat: VoiceflowChat };
  }
}

let loader: Promise<VoiceflowChat> | null = null;

export const loadVoiceflowWidget = (): Promise<VoiceflowChat> => {
  if (loader) {
    return loader;
  }

  loader = new Promise<VoiceflowChat>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = WIDGET_SRC;
    script.type = 'text/javascript';
    script.async = true;

    script.onload = () => {
      if (window.voiceflow?.chat) {
        resolve(window.voiceflow.chat);
      } else {
        reject(new Error('Voiceflow loaded but did not register window.voiceflow.chat'));
      }
    };

    script.onerror = () => {
      // Drop the cached promise so a later visit can retry after a network blip.
      loader = null;
      script.remove();
      reject(new Error('Failed to load the Voiceflow widget script'));
    };

    document.body.appendChild(script);
  });

  return loader;
};

/**
 * `destroy` throws when nothing is mounted, which is the normal state before the
 * first load, so callers always guard it.
 */
export const destroyVoiceflowWidget = () => {
  try {
    window.voiceflow?.chat?.destroy();
  } catch {
    // Nothing was mounted - there is nothing to tear down.
  }
};

/** The user details the apps pass to the assistant alongside the role. */
export interface VoiceflowUserVariables {
  fname: string;
  lname: string;
  email: string;
}

/** Placeholder details the demo page launches with until they are edited. */
export const DEMO_USER_VARIABLES: VoiceflowUserVariables = {
  fname: 'Isaac',
  lname: 'Adam',
  email: 'isaac.adam@example.com',
};

/**
 * The variables handed to the assistant on launch. Kept as its own function so
 * the demo page can show the caller exactly what it sends.
 */
export const buildLaunchPayload = (role: VoiceflowUserRole, user: VoiceflowUserVariables) => ({
  user_role: role,
  fname: user.fname.trim(),
  lname: user.lname.trim(),
  email: user.email.trim(),
});

export const buildVoiceflowConfig = (
  role: VoiceflowUserRole,
  user: VoiceflowUserVariables,
  target: HTMLElement
) => ({
  verify: { projectID: VOICEFLOW_PROJECT_ID },
  url: VOICEFLOW_RUNTIME_URL,
  // Left unset by default so the widget resolves the project's own version.
  // Pinning it (e.g. to "production") fails unless the assistant is published
  // to that environment.
  ...(VOICEFLOW_VERSION_ID ? { versionID: VOICEFLOW_VERSION_ID } : {}),
  voice: { url: VOICEFLOW_VOICE_URL },
  // A per-role user keeps each variant's transcript separate in Voiceflow.
  userID: `lyfe-admin-demo-${role}`,
  // In-memory persistence means switching roles starts a clean conversation
  // instead of replaying the previous role's messages from local storage.
  assistant: { persistence: 'memory' },
  launch: {
    event: {
      type: 'launch',
      payload: buildLaunchPayload(role, user),
    },
  },
  render: {
    mode: 'embedded',
    target,
  },
  autostart: true,
});
